/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictProvider = require('pict-provider');

/**
 * Show/Hide fields config example:
 * FieldDisplaySets:
 * {
 * 		"Default":
 * 		{
 * 			"Sample":
 * 			{
 * 				"Hide": ["IDLineItem", "Station", "Offset"],
 * 				"Show": ["Name", "QRCode", "IDMaterial"]
 * 			}
 * 		},
 * 		"List":
 * 		{
 * 			"Sample":
 * 			{
 * 				"Show": ["Name", "QRCode", "IDMaterial"]
 * 			}
 * 		},
 * 		"Edit":
 * 		{
 * 		},
 * 		"Create":
 * 		{
 * 		},
 * 		"Read":
 * 		{
 * 		},
 * }
 */

class FieldSettingsProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
        this.fieldNamespaces = {};
	}

	/**
	 * Registers a set of fields with a namespace.
	 * For each field, creates a settings object with default values.
	 * Currently the only settings field is "hide" but more can be added as needed.
	 * @param {String} name
	 * @param {Array<String>} fields
	 */
	registerNamespace(name, fields)
	{
		this.fieldNamespaces[name] = { };
		for (const f of fields)
		{
			this.fieldNamespaces[name][f] =
			{
				hide: false,
			};
		}
	}

	/**
	 * Registers a namespace using a schema to derive fields.
	 * @param {String} name
	 * @param {Object} schema
	 */
	registerNamespaceBySchema(name, schema)
	{
		this.registerNamespace(name, Object.keys(schema.properties));
	}

	/**
	 * Registers a namespace by looking up an entity name to pull the schema and generate the field list.
	 * @param {String} name - namespace being used for the field set.
	 * @param {String} entity - the entity we are getting the fields for.
	 * @param {String} [viewType] - the type of view (List/Edit/Read/Create). If not provided we will only use default config.
	 * @param {Boolean} [defaultToShow] - Should fields default to showing? I.e. do we use the "hide specific fields" config vs. the "only show these specific fields" config.
	 * @param {Array<string>} [defaultFields] - A default set of fields to show. Only applicable if defaultToShow is false.
	 */
	async registerNamespaceByEntity(name, entity, viewType, defaultToShow = true, defaultFields = [ 'Name' ])
	{
		try
		{
			const schema = await new Promise((resolve, reject) =>
			{
				this.pict.HeadlightRestClient.getJSON(`${ entity }/Schema`, (error, result) =>
				{
					if (error)
					{
						reject(error);
					}
					else
					{
						resolve(result);
					}
				});
			});

			const config = await new Promise((resolve, reject) =>
			{
				this.pict.HeadlightRestClient.getJSON(`Configuration/For/FieldDisplaySets`, (error, result) =>
				{
					if (error)
					{
						reject(error);
					}
					else
					{
						resolve(result);
					}
				});
			});

			const fields = Object.keys(schema.properties);

			this.registerNamespace(name, fields);

			const configFields = defaultToShow ?
								(config?.[viewType || 'Default']?.[entity]?.['Hide'] || config?.['Default']?.[entity]?.['Hide'] || []) :
								(config?.[viewType || 'Default']?.[entity]?.['Show'] || config?.['Default']?.[entity]?.['Show'] || []);
			if (defaultToShow)
			{
				// If fields should default to shown, just hide the ones in the "Hide" config.
				this.hideFields(name, configFields);
			}
			else
			{
				// Otherwise, hide all the fields not in the "Show" config. If nothing is specified in the show config then default to only showing the name.
				if (!configFields.length)
				{
					this.hideFields(name, fields.filter((f) =>
					{
						return !defaultFields.includes(f);
					}));
				}
				else
				{
					this.hideFields(name, fields.filter((f) =>
					{
						return !configFields.includes(f);
					}));
				}
			}
		}
		catch (e)
		{
			this.pict.log.error(`Failed to get the field information for ${ entity }: `, e);
		}
	}

	/**
	 * Checks if a namespace has already been instantiated.
	 * @param {String} name
	 * @return {Boolean}
	 */
	checkNamespace(name)
	{
		return this.fieldNamespaces[name] ? true : false;
	}

	/**
	 * Sets the hide setting to true for the specified fields in the given namespace.
	 * @param {String} name
	 * @param {Array<String>} fields
	 */
	hideFields(name, fields)
	{
		this.fieldNamespaces[name] = this.fieldNamespaces[name] || {};
		for (const f of fields)
		{
			this.fieldNamespaces[name][f] = this.fieldNamespaces[name][f] || {};
			this.fieldNamespaces[name][f].hide = true;
		}
	}

	/**
	 * Sets the hide setting to false for the specified fields in the given namespace.
	 * @param {String} name
	 * @param {Array<String>} fields
	 */
	unhideFields(name, fields)
	{
		this.fieldNamespaces[name] = this.fieldNamespaces[name] || {};
		for (const f of fields)
		{
			this.fieldNamespaces[name][f] = this.fieldNamespaces[name][f] || {};
			this.fieldNamespaces[name][f].hide = false;
		}
	}

	/**
	 * Sets the hide setting to true for one field in the given namespace.
	 * @param {String} name
	 * @param {String} field
	 */
	hideField(name, field)
	{
		this.hideFields(name, [ field ]);
	}

	/**
	 * Sets the hide setting to false for one field in the given namespace.
	 * @param {String} name
	 * @param {String} field
	 */
	unhideField(name, field)
	{
		this.unhideFields(name, [ field ]);
	}

	/**
	 * Returns if a field within a namespace is hidden or not.
	 * @param {String} name
	 * @param {String} field
	 * @return {Boolean}
	 */
	isHidden(name, field)
	{
		return this.fieldNamespaces?.[name]?.[field]?.hide || false;
	}

	/**
	 * Returns the set of fields that are not hidden for a namespace.
	 * @param {String} name
	 * @return {Array<String>}
	 */
	getShownFields(name)
	{
		return this.fieldNamespaces?.[name] ?
			Object.keys(this.fieldNamespaces[name]).filter((field) =>
			{
				return !this.isHidden(name, field);
			}) : [];
	}

	/**
	 * Returns the set of fields that are hidden for a namespace.
	 * @param {String} name
	 * @return {Array<String>}
	 */
	getHiddenFields(name)
	{
		return this.fieldNamespaces?.[name] ?
			Object.keys(this.fieldNamespaces[name]).filter((field) =>
			{
				return this.isHidden(name, field);
			}) : [];
	}
}

module.exports = FieldSettingsProvider;
module.exports.default_configuration = {};
